/**
 * Best-effort extractor for a newsletter template's **editable zones**.
 *
 * The backend has no zone manifest: a newsletter's `content` is a flat `{ key: value }` bag that the
 * server renders through the template's Twig (`NewsletterRenderer`, `strict_variables=false`,
 * `autoescape='html'`). So the "editable zones" of a template are simply the Twig variables its body
 * references — minus the system chrome the renderer injects (`logo_url`, `unsubscribe_url`) and the
 * loop/assignment locals that aren't content.
 *
 * This parses the Twig with regexes (not a full Twig grammar) and is deliberately conservative:
 *   • `{{ name }}` / `{{ name.prop }}` / `{{ name|filter }}`  → a scalar zone (kind `text`).
 *   • `{{ name|raw }}` (anywhere in the output expr)          → a prose zone (kind `html`).
 *   • `{% for item in name %}…{% endfor %}`                   → a list zone; `item.field` accesses
 *                                                               inside become the item sub-fields.
 *   • `{% if name %}` / `{% elseif name %}`                   → a scalar zone (the condition root).
 *
 * `{% macro %}` bodies are stripped and their names/params, plus `{% import … as x %}` / `{% from …
 * import y %}` aliases, are treated as locals — so a macro's internal `{{ label }}` never surfaces as
 * a phantom zone. Variables a macro is CALLED with (`{{ _self.cta(cta_label) }}`) are real content
 * but are NOT extracted as fields (the call leads with the reserved `_self`/alias).
 *
 * It captures the LEADING identifier of each expression, so a variable buried mid-expression
 * (`{{ prefix ~ name }}` → only `prefix`), a second identifier in a condition (`{% if a and b %}` →
 * only `a`), a `{% set x = y %}` right-hand side, or a macro-call argument can be missed. The editor
 * pairs this with a raw-JSON mode (edit the whole `content` object directly) and preserves unknown
 * existing `content` keys, so a missed zone is never un-editable — it just isn't given its own field.
 */

export type ZoneKind = 'text' | 'html' | 'list';

export interface Zone {
  /** The content key — the Twig variable name. */
  name: string;
  kind: ZoneKind;
  /** For `list` zones only: the sub-fields accessed on each item (e.g. `['name','url']`). Empty
   *  means the items are used bare (`{{ item }}`) → a list of scalars. */
  itemFields: string[];
}

/** Injected by the renderer and always wins over content — never an editable zone. */
const SYSTEM_ZONES = new Set(['logo_url', 'unsubscribe_url']);

/**
 * Twig keywords, literals, globals and tests that can appear as a LEADING identifier but are not
 * content. Filter names (`upper`, `title`, `date`, …) are deliberately NOT here — a filter only
 * appears after `|`, never as the root, and many (`title`, `date`) are perfectly good zone names.
 * Function calls (`path('x')`, `max(a, b)`) are skipped structurally in {@link leadingIdentifier}.
 */
const RESERVED = new Set([
  'true', 'false', 'none', 'null', 'and', 'or', 'not', 'in', 'is', 'as', 'if', 'else', 'elseif',
  'endif', 'for', 'endfor', 'set', 'endset', 'block', 'endblock', 'with', 'endwith', 'apply',
  'endapply', 'filter', 'endfilter', 'autoescape', 'endautoescape', 'spaceless', 'endspaceless',
  'verbatim', 'endverbatim', 'macro', 'endmacro', 'import', 'from', 'embed', 'endembed', 'use',
  'include', 'extends', 'do', 'flush', 'loop', 'parent', '_self', '_context', '_charset',
  'defined', 'empty', 'even', 'odd', 'divisibleby', 'sameas', 'constant', 'iterable', 'starts',
  'ends', 'matches', 'same',
]);

/** Precedence when a name appears under more than one kind: list beats html beats text. */
const KIND_RANK: Record<ZoneKind, number> = { text: 0, html: 1, list: 2 };

interface Acc {
  kind: ZoneKind;
  itemFields: Set<string>;
  index: number;
}

/**
 * The root variable of a Twig sub-expression — the leading identifier of `name`, `name.prop`,
 * `name|filter` — or null when the expression leads with a literal/number/paren or a function call
 * (`path('x')`, `max(a, b)`), none of which is an editable content variable.
 */
const leadingIdentifier = (expr: string): string | null => {
  const stripped = expr.replace(/^[-\s]+/, '');
  const match = stripped.match(/^([A-Za-z_][A-Za-z0-9_]*)/);
  if (match === null) {
    return null;
  }
  const name = match[1];
  if (/^\s*\(/.test(stripped.slice(name.length))) {
    return null; // function call, not a content variable
  }
  return name;
};

const isCandidate = (name: string, locals: Set<string>): boolean =>
  !RESERVED.has(name) && !SYSTEM_ZONES.has(name) && !locals.has(name);

export function extractZones(twig: string): Zone[] {
  if (!twig) {
    return [];
  }
  // Drop comments so a commented-out `{{ x }}` isn't mistaken for a zone.
  const src = twig.replace(/\{#[\s\S]*?#\}/g, '');

  const zones = new Map<string, Acc>();
  const locals = new Set<string>();

  const bump = (name: string, kind: ZoneKind, index: number, itemFields?: string[]) => {
    const existing = zones.get(name);
    if (existing === undefined) {
      zones.set(name, { kind, itemFields: new Set(itemFields ?? []), index });
      return;
    }
    if (KIND_RANK[kind] > KIND_RANK[existing.kind]) {
      existing.kind = kind;
    }
    itemFields?.forEach((f) => existing.itemFields.add(f));
    existing.index = Math.min(existing.index, index);
  };

  // --- Pass 0: macros & imports. A macro's params and its BODY are self-contained (a macro sees
  //     only its arguments, not the content bag), so strip macro bodies from what we scan and record
  //     the macro name + params as locals — otherwise a macro's internal `{{ label }}` would surface
  //     as a phantom zone. Import aliases (`{% import _self as ui %}`, `{% from _self import cta %}`)
  //     are likewise locals, not content. (Variables a macro is CALLED with are real content but are
  //     not extracted as fields — they're reachable via the editor's raw-JSON mode.)
  const scanSrc = src.replace(
    /\{%-?\s*macro\s+([A-Za-z_]\w*)\s*\(([^)]*)\)\s*-?%\}[\s\S]*?\{%-?\s*endmacro\s*-?%\}/g,
    (_match, name: string, params: string) => {
      locals.add(name);
      params
        .split(',')
        .map((p) => p.replace(/=.*/, '').trim())
        .forEach((p) => {
          if (p !== '') {
            locals.add(p);
          }
        });
      return '';
    },
  );
  for (const m of Array.from(scanSrc.matchAll(/\{%-?\s*import\s+[\s\S]+?\s+as\s+([A-Za-z_]\w*)\s*-?%\}/g))) {
    locals.add(m[1]);
  }
  for (const m of Array.from(scanSrc.matchAll(/\{%-?\s*from\s+[\s\S]+?\s+import\s+([^%]+?)\s*-?%\}/g))) {
    m[1].split(',').forEach((part) => {
      const bound = part.trim().split(/\s+as\s+/).pop()?.trim() ?? '';
      if (/^[A-Za-z_]\w*$/.test(bound)) {
        locals.add(bound);
      }
    });
  }

  // --- Pass 1: collect assignment locals so they're never emitted as zones. ---
  // `{% set name = … %}` and the `{% set name %}…{% endset %}` block form.
  for (const m of Array.from(scanSrc.matchAll(/\{%-?\s*set\s+([A-Za-z_]\w*)/g))) {
    locals.add(m[1]);
  }
  // Loop locals — captured before we scan loop bodies, so nested/earlier output tags see them.
  for (const m of Array.from(scanSrc.matchAll(/\{%-?\s*for\s+([A-Za-z_]\w*(?:\s*,\s*[A-Za-z_]\w*)?)\s+in\s+/g))) {
    m[1].split(',').forEach((name) => locals.add(name.trim()));
  }

  // --- Pass 2: list zones from `{% for locals in COLLECTION %}…{% endfor %}`. ---
  const forBlock = /\{%-?\s*for\s+([A-Za-z_]\w*(?:\s*,\s*[A-Za-z_]\w*)?)\s+in\s+([^%]+?)\s*-?%\}([\s\S]*?)\{%-?\s*endfor\s*-?%\}/g;
  for (const m of Array.from(scanSrc.matchAll(forBlock))) {
    const loopLocals = m[1].split(',').map((s) => s.trim());
    const collectionRoot = leadingIdentifier(m[2]);
    const body = m[3];
    // Sub-fields: `item.field` accesses on any of the loop's locals, inside this loop body.
    const itemFields: string[] = [];
    for (const local of loopLocals) {
      const fieldRe = new RegExp(`\\b${local}\\.([A-Za-z_]\\w*)`, 'g');
      for (const fm of Array.from(body.matchAll(fieldRe))) {
        if (!itemFields.includes(fm[1])) {
          itemFields.push(fm[1]);
        }
      }
    }
    // A collection that is itself a local (nested loop over `outer.children`) is excluded by
    // isCandidate, so only top-level content collections become list zones.
    if (collectionRoot !== null && isCandidate(collectionRoot, locals)) {
      bump(collectionRoot, 'list', m.index ?? 0, itemFields);
    }
  }

  // --- Pass 3: scalar/prose zones from output tags `{{ … }}`. ---
  for (const m of Array.from(scanSrc.matchAll(/\{\{-?\s*([\s\S]*?)\s*-?\}\}/g))) {
    const expr = m[1];
    const root = leadingIdentifier(expr);
    if (root === null || !isCandidate(root, locals)) {
      continue;
    }
    // `|raw` (with optional spaces) anywhere in the expression → the author opted this zone into HTML.
    const kind: ZoneKind = /\|\s*raw\b/.test(expr) ? 'html' : 'text';
    bump(root, kind, m.index ?? 0);
  }

  // --- Pass 4: condition roots from `{% if … %}` / `{% elseif … %}`. ---
  for (const m of Array.from(scanSrc.matchAll(/\{%-?\s*(?:if|elseif)\s+([^%]+?)\s*-?%\}/g))) {
    const root = leadingIdentifier(m[1]);
    if (root !== null && isCandidate(root, locals)) {
      bump(root, 'text', m.index ?? 0);
    }
  }

  return Array.from(zones.entries())
    .map(([name, acc]) => ({ name, kind: acc.kind, itemFields: Array.from(acc.itemFields), index: acc.index }))
    .sort((a, b) => a.index - b.index)
    .map(({ name, kind, itemFields }) => ({ name, kind, itemFields }));
}
